<div class="flex flex-col max-h-[90vh]">

  <!-- Header -->
  <div class="flex flex-row items-start justify-between gap-4 px-6 py-4 border-b border-gray-100">
    <div class="flex items-center gap-3 min-w-0">
      @if (data.image) {
        <img [src]="data.image.includes(',') ? data.image.split(',')[0] : data.image" [alt]="data.productName" class="h-12 w-12 rounded-xl object-cover border border-gray-200 flex-shrink-0" />
      } @else {
        <span class="h-12 w-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0"><mat-icon>history</mat-icon></span>
      }
      <div class="min-w-0">
        <div class="text-[11px] font-semibold uppercase tracking-wider text-amber-600">Stock history</div>
        <h1 class="text-lg font-bold text-gray-900 leading-tight truncate">{{ data.productName }}@if (data.label) { <span class="ml-2 align-middle text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{{ data.label }}</span> }</h1>
        <div class="text-xs text-gray-400 flex items-center gap-3 mt-0.5">
          @if (data.sku) { <button class="inline-flex items-center gap-1 font-mono hover:text-amber-700" (click)="copySku()" matTooltip="Copy SKU"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">tag</mat-icon>{{ data.sku }}</button> }
          @if (data.barcode) { <span class="inline-flex items-center gap-1 font-mono"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">barcode</mat-icon>{{ data.barcode }}</span> }
        </div>
      </div>
    </div>
    <button class="h-9 w-9 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800 flex items-center justify-center flex-shrink-0 transition-colors" (click)="close()" aria-label="Close"><mat-icon class="!text-[20px]">close</mat-icon></button>
  </div>

  @if (loading && !result) {
    <div class="flex justify-center items-center min-h-[320px]"><mat-spinner diameter="32"></mat-spinner></div>
  }
  @if (loadError) {
    <div class="m-6 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm flex items-center justify-between">
      <span>Could not load the stock history.</span>
      <button class="text-xs uppercase font-semibold underline" (click)="load()">Try again</button>
    </div>
  }

  @if (result) {
  <!-- Where stock stands now -->
  <div class="px-6 pt-4 pb-3 flex flex-row items-stretch gap-3 flex-wrap">
    <div class="rounded-2xl bg-gray-50 border border-gray-100 px-4 py-2.5 min-w-[110px]">
      <div class="text-[11px] uppercase tracking-wider text-gray-400">Kettering</div>
      <div class="text-xl font-bold text-gray-900">{{ result.actual.Kettering | number }}</div>
    </div>
    <div class="rounded-2xl bg-gray-50 border border-gray-100 px-4 py-2.5 min-w-[110px]">
      <div class="text-[11px] uppercase tracking-wider text-gray-400">Markstey</div>
      <div class="text-xl font-bold text-gray-900">{{ result.actual.Markstey | number }}</div>
    </div>
    <div class="rounded-2xl bg-neutral-900 px-4 py-2.5 min-w-[110px]">
      <div class="text-[11px] uppercase tracking-wider text-neutral-400">On hand now</div>
      <div class="text-xl font-bold text-amber-400">{{ result.actual.total | number }}</div>
    </div>
    @if (trend; as t) {
    <div class="rounded-2xl bg-gray-50 border border-gray-100 px-4 py-2.5 hidden md:block" matTooltip="Total on hand across the movements shown below (oldest on the left)">
      <div class="text-[11px] uppercase tracking-wider text-gray-400">Trend</div>
      <svg width="120" height="30" viewBox="0 0 120 32" class="mt-0.5"><polyline [attr.points]="t.points" fill="none" stroke="#f59e0b" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" /></svg>
    </div>
    }
    <div class="ml-auto self-center">
      @if (!hasGap) {
        <span class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700" matTooltip="Adding up every recorded movement gives exactly today's stock">
          <mat-icon class="!text-[16px] !h-4 !w-4">verified</mat-icon> History adds up to live stock
        </span>
      } @else {
        <button class="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors" (click)="showGapHelp = !showGapHelp">
          <mat-icon class="!text-[16px] !h-4 !w-4">warning</mat-icon>
          Some changes were not recorded:
          @if (gapKettering) { Kettering {{ gapKettering > 0 ? '+' : '' }}{{ gapKettering }} }
          @if (gapKettering && gapMarkstey) { , }
          @if (gapMarkstey) { Markstey {{ gapMarkstey > 0 ? '+' : '' }}{{ gapMarkstey }} }
        </button>
      }
    </div>
  </div>
  @if (hasGap && showGapHelp) {
  <div class="mx-6 mb-3 text-xs text-amber-900 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3">
    Adding up every movement recorded here does not reach the stock the shop holds now. It means stock was changed somewhere that does not write to this history (for example straight in the database, or before history started). Nothing is wrong with the rows below - some are just missing.
  </div>
  }

  <!-- Filters -->
  <div class="px-6 pb-3 flex flex-col gap-2.5">
    <div class="flex items-center gap-x-4 gap-y-2 flex-wrap">
      <div class="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
        @for (y of yards; track y.value) {
        <button class="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all" [class]="warehouse === y.value ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'" (click)="setYard(y.value)">{{ y.label }}</button>
        }
      </div>
      <div class="flex items-center gap-1.5">
        <button class="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all" [class]="direction === 'in' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'" (click)="setDirection('in')"><mat-icon class="!text-[14px] !h-[14px] !w-[14px]">south_west</mat-icon> Stock in</button>
        <button class="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all" [class]="direction === 'out' ? 'bg-rose-500 text-white border-rose-500' : 'bg-white text-gray-600 border-gray-200 hover:border-rose-300 hover:bg-rose-50'" (click)="setDirection('out')"><mat-icon class="!text-[14px] !h-[14px] !w-[14px]">north_east</mat-icon> Stock out</button>
      </div>
      <div class="ml-auto flex items-center gap-2">
        @if (filtersActive) { <button class="text-xs font-semibold text-gray-500 hover:text-red-600" (click)="clearFilters()">Clear filters</button> }
        <button class="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:border-amber-300 hover:bg-amber-50 transition-colors disabled:opacity-50" [disabled]="exporting || !result.count" (click)="exportHistory()" matTooltip="Download these movements as an Excel file">
          <mat-icon class="!text-[16px] !h-4 !w-4">download</mat-icon> {{ exporting ? 'Preparing…' : 'Download' }}
        </button>
      </div>
    </div>
    <div class="flex items-center gap-1.5 flex-wrap">
      @for (g of groups; track g.value) {
      <button class="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border transition-all"
        [class]="group === g.value ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300 hover:bg-amber-50'" (click)="setGroup(g.value)">
        <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">{{ g.icon }}</mat-icon>{{ g.label }}
      </button>
      }
    </div>
  </div>

  <!-- Timeline -->
  <div class="overflow-auto flex-1 border-t border-gray-100 bg-gray-50/40 transition-opacity" [class.opacity-60]="loading">
    @if (days.length) {
      @for (day of days; track day.key) {
      <div class="sticky top-0 z-10 px-6 py-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100/95 backdrop-blur border-b border-gray-200">{{ day.title }}</div>
      @for (m of day.rows; track m.id) {
      <div class="flex items-center gap-4 px-6 py-3 border-b border-gray-100 bg-white hover:bg-amber-50/30 transition-colors">
        <span class="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0" [class]="tint(m)"><mat-icon class="!text-[20px] !h-5 !w-5">{{ icon(m) }}</mat-icon></span>

        <div class="min-w-0 flex-1">
          <div class="text-sm font-semibold text-gray-900">{{ sentence(m) }}</div>
          <div class="mt-0.5 flex items-center flex-wrap gap-x-2 gap-y-0.5 text-xs text-gray-500">
            <span>{{ typeLabel(m) }}</span>
            @if (m.warehouse !== 'Total') { <span class="text-gray-300">·</span><span>{{ m.warehouse }}</span> }
            @if (m.referenceLabel) {
              <span class="text-gray-300">·</span>
              @if (canOpen(m)) {
                <button class="font-semibold text-amber-700 hover:underline inline-flex items-center gap-0.5" (click)="openReference(m)" [matTooltip]="m.referenceType === 'ORDER' ? 'View the order' : 'Open the purchase order'">{{ m.referenceLabel }}<mat-icon class="!text-[13px] !h-[13px] !w-[13px]">open_in_new</mat-icon></button>
              } @else { <span class="font-medium text-gray-700">{{ m.referenceLabel }}</span> }
            }
          </div>
          @if (reason(m)) {
          <div class="mt-1 inline-flex items-start gap-1.5 text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-2.5 py-1"><mat-icon class="!text-[14px] !h-[14px] !w-[14px] mt-px text-gray-400">sticky_note_2</mat-icon>{{ reason(m) }}</div>
          }
        </div>

        <!-- The change and where it left the yard -->
        <div class="text-right flex-shrink-0 w-28">
          <div class="text-lg font-bold leading-none" [class]="m.quantityChange > 0 ? 'text-emerald-600' : m.quantityChange < 0 ? 'text-rose-600' : 'text-gray-400'">{{ changeText(m) }}</div>
          <div class="text-[11px] text-gray-400 mt-1">left {{ m.balanceAfter | number }}{{ m.warehouse === 'Total' ? '' : ' at ' + m.warehouse }}</div>
        </div>

        <div class="text-right flex-shrink-0 w-28 hidden sm:block">
          <div class="text-xs font-semibold text-gray-700" [matTooltip]="(m.createdAt | date: 'd MMM y, HH:mm:ss')!">{{ m.createdAt | date: 'HH:mm' }}</div>
          <div class="text-[11px] text-gray-400 mt-0.5 truncate" [matTooltip]="m.createdBy || 'the system'">by {{ who(m) }}</div>
        </div>
      </div>
      }
      }
    } @else if (!loading) {
      <div class="py-16 text-center">
        <span class="mx-auto h-14 w-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3"><mat-icon class="!text-amber-500 !text-[30px] !h-[30px] !w-[30px]">history</mat-icon></span>
        <div class="text-base font-semibold text-gray-800">{{ filtersActive ? 'No movements match these filters' : 'No stock movements recorded yet' }}</div>
        <div class="text-sm text-gray-500 mt-1">{{ filtersActive ? 'Try a different yard or type of movement' : 'Sales, deliveries, counts and edits will appear here' }}</div>
        @if (filtersActive) { <button class="mt-3 text-sm font-semibold text-amber-700 hover:underline" (click)="clearFilters()">Clear filters</button> }
      </div>
    }
  </div>

  <mat-paginator class="border-t border-gray-100"
    [length]="result.count" [pageSize]="pageSize" [pageIndex]="pageIndex"
    [pageSizeOptions]="[25, 50, 100]" showFirstLastButtons (page)="onPage($event)">
  </mat-paginator>
  }
</div>
